import React, { memo } from "react";
import { IonIcon, IonImg, IonItem } from "@ionic/react";
import { formatCurrency } from '../helpers/currencyHelpers';
import './ModifierGroupItem.scss'
import AltQuantityInput from "./AltQuantityInput";
import { baseUrl, cacheBust } from "../helpers/configHelper";
import { checkmarkOutline } from "ionicons/icons";
import { inlineBrandImage } from "../helpers/menuHelpers";

interface ModifierGroupItemCommonProps {
    showImage?: boolean;
    showPrice?: boolean;
    showQuantityComponent?: boolean;

    menuItemName: string;
    sku: string;
    price: number;
    kcal?: number;
    hasBrandImage: boolean;
    productFlags: string[];
}

function ModifierGroupItemCommon(props: ModifierGroupItemCommonProps) {
    function getMenuItemName(): string {
        return inlineBrandImage(props.menuItemName, true);
    }

    return (
        <div className='flex details'>
            {props.showImage &&
                <IonImg
                    className='mgi-img'
                    alt=""
                    src={`${baseUrl()}/images/ProductShots/${props.sku}_PDP.png?v=${cacheBust()}`}
                />
            }
            <div className={`details-text ${props.showQuantityComponent ? 'details-limit-width' : ''}`}>
                <p>
                    <span className="align-children" dangerouslySetInnerHTML={{ __html: getMenuItemName() }}></span>

                    {props.showPrice && props.price > 0 &&
                        <span className='no-wrap'>
                            {` + ${formatCurrency(props.price)}`}
                        </span>
                    }
                </p>

                
                    <span className="menu-item-kcal align-children">
                        {props.productFlags?.map((v: string) => {
                            return (
                                <IonImg className="product-flag" src={`${baseUrl()}/images/assets/${v}.png`} />
                            )
                        })}
                        {(props.kcal ?? 0) > 0 &&
                            ` (${props.kcal}kcal)`
                        }
                    </span>
                
            </div>
        </div>
    );
}

interface IModifierGroupItemProps {
    inStock: boolean;

    canSelectMultiple?: boolean;
    showPrice?: boolean;
    showImage?: boolean;
    showQuantityComponent?: boolean;

    menuItem: IMopModifierGroupItem;
    disabled?: boolean;

    onSelect?: (menuItemName: string) => void;
    onChange?: (menuItem: IMopModifierGroupItem, quantity: number) => void;

    disableSelect?: boolean;
    disableAddQuantityButton?: boolean;
}

// NOTE: React normally re-renders a component whenever the parent re-renders.
// memo will not re-render when the parent re-renders as long as the new props
// are the same as old props.
const ModifierGroupItem = memo(function ModifierGroupItem(props: IModifierGroupItemProps) {
    function handleItemSelected() {
        if (props.canSelectMultiple) {
            if (props.onChange) {
                props.onChange(props.menuItem, props.menuItem.quantitySelected ? 0 : 1);
            }
        } else {
            if (props.onSelect) {
                props.onSelect(props.menuItem.menuItemName);
            }
        }
    }

    function handleItemQuantity(quantity: number) {
        if (props.onChange) {
            props.onChange(props.menuItem, quantity);
        }
    }

    return (
        <div className="modifier-item-wrapper modifier-item">
            {props.showQuantityComponent
                ?
                <div className={`temp ${props.menuItem.quantitySelected > 0 ? 'selected' : ''}`}>
                    <div className='checkbox-container'>
                        {props.menuItem.quantitySelected > 0 &&
                            <IonIcon icon={checkmarkOutline} color="primary" />
                        }
                    </div>
                    <ModifierGroupItemCommon
                        showImage={props.showImage}
                        showPrice={props.showPrice}
                        showQuantityComponent={props.showQuantityComponent}
                        menuItemName={props.menuItem.menuItemName}
                        sku={props.menuItem.sku}
                        price={props.menuItem.price ?? 0}
                        kcal={props.menuItem.kcal}
                        hasBrandImage={props.menuItem.hasBrandImage}
                        productFlags={props.menuItem.productFlags}
                    />
                    <div
                        className="selector"
                        onClick={handleItemSelected}
                    />
                    <div className="quantity">
                        <AltQuantityInput
                            value={props.menuItem.quantitySelected}
                            onChange={handleItemQuantity}
                            disableAddQuantityButton={props.disableAddQuantityButton}
                        />
                    </div>
                </div>
                :
                <IonItem
                    className={props.menuItem.quantitySelected > 0 ? "checked" : ""}
                    disabled={props.disabled || (props.canSelectMultiple && props.disableSelect)}
                    lines="none"
                    onClick={handleItemSelected}
                >
                    <div className='checkbox-container'>
                        {props.menuItem.quantitySelected > 0 &&
                            <IonIcon icon={checkmarkOutline} color="primary" />
                        }
                    </div>
                    <ModifierGroupItemCommon
                        showImage={props.showImage}
                        showPrice={props.showPrice}
                        showQuantityComponent={props.showQuantityComponent}
                        menuItemName={props.menuItem.menuItemName}
                        sku={props.menuItem.sku}
                        price={props.menuItem.price ?? 0}
                        kcal={props.menuItem.kcal}
                        hasBrandImage={props.menuItem.hasBrandImage}
                        productFlags={props.menuItem.productFlags}
                    />
                </IonItem>
            }
            {props.inStock === false &&
                <>
                    <div className='out-of-stock-bg'>
                    </div>
                    <div className='out-of-stock'>
                        <p>Sorry, currently unavailable</p>
                    </div>
                </>
            }
        </div>
    );
});

export default ModifierGroupItem;
